import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Divider,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  VideoLibrary as VideoIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import ProfessionalTable from '../components/ProfessionalTable';
import AdminPageWrapper from './components/AdminPageWrapper';
import api from '../utils/api';
import { API } from '../config/apiConfig';

function AdminVideoCategories() {
  const [videoCategories, setVideoCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  
  // Dialog states
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [subcategoryDialog, setSubcategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({
    category: '',
    wasabiContainer: ''
  });
  
  const [subcategoryForm, setSubcategoryForm] = useState({
    categoryId: '',
    subcategoryName: '',
    wasabiContainer: '',
    isActive: true,
    displayOrder: 0
  });
  
  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load data
  useEffect(() => {
    loadVideoCategories();
  }, []);

  const loadVideoCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API.adminVideoCategories || '/api/admin/video-categories'}`);
      setVideoCategories(response.data.data || []);
    } catch (error) {
      console.error('❌ Error loading video categories:', error);
      showSnackbar('Failed to load video categories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadVideoCategoryDetails = async (videoCategoryId) => {
    try {
      const response = await api.get(`${API.adminVideoCategories || '/api/admin/video-categories'}/${videoCategoryId}`);
      setSelectedCategory(response.data.data.videoCategory);
      setSubcategories(response.data.data.subcategories || []);
    } catch (error) {
      console.error('❌ Error loading video category details:', error);
      showSnackbar('Failed to load video category details', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedTab(1); // Switch to Subcategories tab
    loadVideoCategoryDetails(category.VideoCategoryId);
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      category: '',
      wasabiContainer: ''
    });
    setCategoryDialog(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      category: category.Category,
      wasabiContainer: category.WasabiContainer
    });
    setCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await api.put(`${API.adminVideoCategories || '/api/admin/video-categories'}/${editingCategory.VideoCategoryId}`, categoryForm);
        showSnackbar('Video category updated successfully');
      } else {
        await api.post(`${API.adminVideoCategories || '/api/admin/video-categories'}`, categoryForm);
        showSnackbar('Video category created successfully');
      }
      setCategoryDialog(false);
      loadVideoCategories();
    } catch (error) {
      console.error('❌ Error saving video category:', error);
      showSnackbar(error.response?.data?.message || 'Failed to save video category', 'error');
    }
  };

  const handleAddSubcategory = () => {
    if (!selectedCategory) {
      showSnackbar('Please select a video category first', 'warning');
      return;
    }
    
    setEditingSubcategory(null);
    setSubcategoryForm({
      categoryId: selectedCategory.VideoCategoryId,
      subcategoryName: '',
      wasabiContainer: '',
      isActive: true,
      displayOrder: 0
    });
    setSubcategoryDialog(true);
  };

  const handleEditSubcategory = (subcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryForm({
      categoryId: subcategory.CategoryId,
      subcategoryName: subcategory.SubcategoryName,
      wasabiContainer: subcategory.WasabiContainer,
      isActive: subcategory.IsActive,
      displayOrder: subcategory.DisplayOrder
    });
    setSubcategoryDialog(true);
  };

  const handleSaveSubcategory = async () => {
    try {
      if (editingSubcategory) {
        await api.put(`${API.adminVideoCategories || '/api/admin/video-categories'}/subcategories/${editingSubcategory.SubcategoryId}`, subcategoryForm);
        showSnackbar('Video subcategory updated successfully');
      } else {
        await api.post(`${API.adminVideoCategories || '/api/admin/video-categories'}/${subcategoryForm.categoryId}/subcategories`, subcategoryForm);
        showSnackbar('Video subcategory added successfully');
      }
      setSubcategoryDialog(false);
      if (selectedCategory) {
        loadVideoCategoryDetails(selectedCategory.VideoCategoryId);
      }
    } catch (error) {
      console.error('❌ Error saving video subcategory:', error);
      showSnackbar(error.response?.data?.message || 'Failed to save video subcategory', 'error');
    }
  };

  const handleDeleteSubcategory = async (subcategory) => {
    if (window.confirm(`Are you sure you want to delete the subcategory "${subcategory.SubcategoryName}"?`)) {
      try {
        await api.delete(`${API.adminVideoCategories || '/api/admin/video-categories'}/subcategories/${subcategory.SubcategoryId}`);
        showSnackbar('Video subcategory deleted successfully');
        if (selectedCategory) {
          loadVideoCategoryDetails(selectedCategory.VideoCategoryId);
        }
      } catch (error) {
        console.error('❌ Error deleting video subcategory:', error);
        showSnackbar('Failed to delete video subcategory', 'error');
      }
    }
  };

  const categoryColumns = [
    {
      header: 'Category',
      accessor: 'Category',
      render: (category, row) => (
        <Box>
          <Typography fontWeight={500}>{category}</Typography>
          <Typography variant="body2" color="text.secondary">
            {row.SubcategoryCount} subcategories
          </Typography>
        </Box>
      )
    },
    {
      header: 'Wasabi Container',
      accessor: 'WasabiContainer',
      render: (container) => (
        <Chip label={container} size="small" color="primary" variant="outlined" />
      ),
      style: { textAlign: 'center' }
    },
    {
      header: 'Actions',
      accessor: 'VideoCategoryId',
      render: (id, row) => (
        <Box display="flex" gap={1} justifyContent="center">
          <Tooltip title="Manage Subcategories">
            <IconButton
              size="small"
              onClick={() => handleCategorySelect(row)}
              color="primary"
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditCategory(row)}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      style: { textAlign: 'center' }
    }
  ];

  const subcategoryColumns = [
    {
      header: 'Subcategory',
      accessor: 'SubcategoryName',
      render: (name, row) => (
        <Box>
          <Typography fontWeight={500}>{name}</Typography>
          <Typography variant="body2" color="text.secondary">
            Order: {row.DisplayOrder}
          </Typography>
        </Box>
      )
    },
    {
      header: 'Wasabi Container',
      accessor: 'WasabiContainer',
      render: (container) => (
        <Chip label={container} size="small" color="secondary" variant="outlined" />
      ),
      style: { textAlign: 'center' }
    },
    {
      header: 'Status',
      accessor: 'IsActive',
      render: (isActive) => (
        <Chip 
          label={isActive ? 'Active' : 'Inactive'}
          color={isActive ? 'success' : 'error'}
          size="small"
        />
      ),
      style: { textAlign: 'center' }
    },
    {
      header: 'Created',
      accessor: 'CreatedAt',
      render: (date) => new Date(date).toLocaleDateString(),
      style: { textAlign: 'center' }
    },
    {
      header: 'Actions',
      accessor: 'SubcategoryId',
      render: (id, row) => (
        <Box display="flex" gap={1} justifyContent="center">
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditSubcategory(row)}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteSubcategory(row)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      style: { textAlign: 'center' }
    }
  ];

  return (
    <AdminPageWrapper 
      title="Video Categories Management" 
      subtitle="Manage video categories and their subcategories"
    >
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateCategory}
        >
          Add Video Category
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label="Video Categories" icon={<CategoryIcon />} />
          <Tab 
            label="Subcategories" 
            icon={<VideoIcon />}
            disabled={!selectedCategory}
          />
        </Tabs>
      </Box>

      {selectedTab === 0 && (
        <ProfessionalTable
          columns={categoryColumns}
          data={videoCategories.map(cat => ({ ...cat, id: cat.VideoCategoryId }))}
          loading={loading}
          emptyMessage="No video categories found"
        />
      )}

      {selectedTab === 1 && selectedCategory && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Subcategories for {selectedCategory.Category}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddSubcategory}
            >
              Add Subcategory
            </Button>
          </Box>
          
          <ProfessionalTable
            columns={subcategoryColumns}
            data={subcategories.map(sub => ({ ...sub, id: sub.SubcategoryId }))}
            loading={false}
            emptyMessage="No subcategories found"
          />
        </Box>
      )}

      {/* Category Dialog */}
      <Dialog open={categoryDialog} onClose={() => setCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Video Category' : 'Add Video Category'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name"
                value={categoryForm.category}
                onChange={(e) => setCategoryForm({ ...categoryForm, category: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Wasabi Container"
                value={categoryForm.wasabiContainer}
                onChange={(e) => setCategoryForm({ ...categoryForm, wasabiContainer: e.target.value })}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveCategory} variant="contained">
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subcategory Dialog */}
      <Dialog open={subcategoryDialog} onClose={() => setSubcategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subcategory Name"
                value={subcategoryForm.subcategoryName}
                onChange={(e) => setSubcategoryForm({ ...subcategoryForm, subcategoryName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Wasabi Container"
                value={subcategoryForm.wasabiContainer}
                onChange={(e) => setSubcategoryForm({ ...subcategoryForm, wasabiContainer: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={subcategoryForm.displayOrder}
                onChange={(e) => setSubcategoryForm({ ...subcategoryForm, displayOrder: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={subcategoryForm.isActive}
                    onChange={(e) => setSubcategoryForm({ ...subcategoryForm, isActive: e.target.checked })}
                  />
                }
                label="Active"
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubcategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveSubcategory} variant="contained">
            {editingSubcategory ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminPageWrapper>
  );
}

export default AdminVideoCategories;
